import axios from 'axios';
import { useQuery, useMutation } from '@tanstack/react-query';

// Partner Portal API hooks. Every request path starts with /portal — that
// prefix is what the axios interceptor keys on to attach the PORTAL bearer
// token instead of the app JWT (full isolation between the two sessions).

export const useRequestPortalCode = (callback, opts = {}) =>
  useMutation(
    async (email) => {
      const res = await axios.post('/portal/auth/request-code', { email });
      return res.data;
    },
    { ...opts, onSuccess: callback || (() => {}) },
  );

export const useVerifyPortalCode = (callback, opts = {}) =>
  useMutation(
    async ({ email, code }) => {
      const res = await axios.post('/portal/auth/verify-code', { email, code });
      return res.data;
    },
    { ...opts, onSuccess: callback || (() => {}) },
  );

export const usePortalLogout = (callback, opts = {}) =>
  useMutation(
    async () => {
      const res = await axios.post('/portal/auth/logout');
      return res.data;
    },
    { ...opts, onSuccess: callback || (() => {}) },
  );

// Continue signing (Nic 2026-10-24): the signed-in applicant's own packet
// link, while their paperwork is unfinished or sent back for a fix.
export const useContinuePortalPaperwork = (opts = {}) =>
  useMutation(async () => {
    const res = await axios.post('/portal/paperwork/continue');
    return res.data;
  }, opts);

export const usePortalMe = (opts = {}) =>
  useQuery(
    ['portal', 'me'],
    async () => {
      const res = await axios.get('/portal/me');
      return res.data;
    },
    { refetchOnWindowFocus: false, staleTime: 15000, ...opts },
  );

export const usePortalSummary = (opts = {}) =>
  useQuery(
    ['portal', 'summary'],
    async () => {
      const res = await axios.get('/portal/summary');
      return res.data;
    },
    { refetchOnWindowFocus: false, staleTime: 15000, ...opts },
  );

export const usePortalReferrals = (page = 1, opts = {}) =>
  useQuery(
    ['portal', 'referrals', { page }],
    async () => {
      const res = await axios.get('/portal/referrals', { params: { page } });
      return res.data;
    },
    { refetchOnWindowFocus: false, keepPreviousData: true, ...opts },
  );

export const usePortalPayouts = (status, page = 1, opts = {}) =>
  useQuery(
    ['portal', 'payouts', { status, page }],
    async () => {
      const res = await axios.get('/portal/payouts', { params: { status, page } });
      return res.data;
    },
    { refetchOnWindowFocus: false, keepPreviousData: true, ...opts },
  );

export const usePortalPaperwork = (opts = {}) =>
  useQuery(
    ['portal', 'paperwork'],
    async () => {
      const res = await axios.get('/portal/paperwork');
      return res.data;
    },
    { refetchOnWindowFocus: false, ...opts },
  );

// Open a signed document in a new tab. The tab opens SYNCHRONOUSLY (before the
// async URL fetch) so popup blockers treat it as user-initiated.
export const openPortalSignedPdf = async (envelopeId) => {
  const tab = window.open('', '_blank');
  try {
    const res = await axios.get(`/portal/paperwork/${envelopeId}/signed-pdf-url`);
    if (tab) tab.location = res.data.url;
    else window.open(res.data.url, '_blank');
  } catch (err) {
    if (tab) tab.close();
    throw err;
  }
};

// The QR PNG requires the portal bearer header, so it can't be an <img src> —
// fetch as a blob and hand back an object URL (caller revokes it).
export const fetchPortalQrObjectUrl = async () => {
  const res = await axios.get('/portal/qr.png', { responseType: 'blob' });
  return URL.createObjectURL(res.data);
};
